<!--
  The buyer's notes on one lot (BXS-3627, M19).

  `@if` / `@for` throughout and no `<ng-container>`: `npm run lint:templates`
  (the BXS-3584 guard) rejects host bindings on container nodes.

  Chips are `<button>` elements with `aria-pressed`, not checkboxes styled to
  look like chips. A screen reader then announces "Balanced, pressed" rather
  than reading a label whose input is visually hidden somewhere else.
-->
<section class="ia-notes" [attr.aria-busy]="loading || saving">
  <header class="ia-notes__head">
    <h2 class="ia-notes__title">
      {{ 'international_auctions.my_list.notes_title' | translate }}
    </h2>
    <p class="ia-notes__sub">
      {{ 'international_auctions.my_list.notes_private' | translate }}
    </p>
  </header>

  @if (!signedIn) {
    <!--
      The gate. Saeed's resolution to Mai's objection was that this feature
      appears once you sign in — so a guest is TOLD it exists and shown the way
      in, rather than shown nothing, which was the original complaint.
    -->
    <div class="ia-notes__gate">
      <p class="ia-notes__gateText">
        {{ 'international_auctions.my_list.sign_in_prompt' | translate }}
      </p>
      <a class="ia-notes__gateLink" [routerLink]="signInLink | localeLink">
        {{ 'international_auctions.my_list.sign_in_cta' | translate }}
      </a>
    </div>
  } @else {
    @if (loading) {
      <p class="ia-notes__loading">
        {{ 'international_auctions.my_list.loading' | translate }}
      </p>
    } @else {
      <div class="ia-notes__field">
        <span class="ia-notes__label" id="ia-notes-rating">
          {{ 'international_auctions.my_list.rating' | translate }}
        </span>
        <div class="ia-notes__chips" role="group" aria-labelledby="ia-notes-rating">
          @for (value of ratings; track value) {
            <button
              type="button"
              class="ia-chipbtn ia-chipbtn--grade"
              [class.is-on]="rating === value"
              [attr.aria-pressed]="rating === value"
              (click)="pickRating(value)"
            >
              {{ value }}
            </button>
          }
        </div>
      </div>

      <div class="ia-notes__field">
        <span class="ia-notes__label" id="ia-notes-conf">
          {{ 'international_auctions.my_list.conformation' | translate }}
        </span>
        <div class="ia-notes__chips" role="group" aria-labelledby="ia-notes-conf">
          @for (value of conformations; track value) {
            <button
              type="button"
              class="ia-chipbtn"
              [class.is-on]="isConformationOn(value)"
              [attr.aria-pressed]="isConformationOn(value)"
              (click)="toggleConformation(value)"
            >
              {{ value }}
            </button>
          }
        </div>
      </div>

      <div class="ia-notes__field">
        <span class="ia-notes__label" id="ia-notes-scope">
          {{ 'international_auctions.my_list.scope' | translate }}
        </span>
        <div class="ia-notes__chips" role="group" aria-labelledby="ia-notes-scope">
          @for (value of scopes; track value) {
            <button
              type="button"
              class="ia-chipbtn"
              [class.is-on]="isScopeOn(value)"
              [attr.aria-pressed]="isScopeOn(value)"
              (click)="toggleScope(value)"
            >
              {{ value }}
            </button>
          }
        </div>
      </div>

      <div class="ia-notes__field">
        <label class="ia-notes__label" for="ia-notes-text">
          {{ 'international_auctions.my_list.free_text' | translate }}
        </label>
        <textarea
          id="ia-notes-text"
          class="ia-notes__textarea"
          rows="4"
          dir="auto"
          [attr.maxlength]="maxLength"
          [attr.placeholder]="
            'international_auctions.my_list.free_text_hint' | translate
          "
          [value]="text"
          (input)="onTextInput($event)"
        ></textarea>
        <!--
          Announced politely rather than assertively: a live counter that
          interrupts on every keystroke makes the field unusable with a screen
          reader.
        -->
        <span class="ia-notes__count" aria-live="polite">
          {{ remaining }}
        </span>
      </div>

      <div class="ia-notes__actions">
        <button
          type="button"
          class="ia-notes__save"
          [disabled]="!dirty || saving"
          (click)="save()"
        >
          @if (saving) {
            {{ 'international_auctions.my_list.saving' | translate }}
          } @else {
            {{ 'international_auctions.my_list.save' | translate }}
          }
        </button>

        @if (hasStoredNote) {
          <button
            type="button"
            class="ia-notes__clear"
            [disabled]="saving"
            (click)="clear()"
          >
            {{ 'international_auctions.my_list.clear' | translate }}
          </button>
        }

        <!--
          One region for both outcomes, so neither shifts the row it sits
          under when it appears.
        -->
        <p class="ia-notes__status" role="status" aria-live="polite">
          @if (failed) {
            <span class="ia-notes__failed">
              {{ 'international_auctions.my_list.failed' | translate }}
            </span>
          } @else if (justSaved) {
            <span class="ia-notes__ok">
              {{ 'international_auctions.my_list.saved' | translate }}
            </span>
          }
        </p>
      </div>
    }
  }
</section>
